import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { createRoot } from 'react-dom/client';
import { store } from '@/src/store/configure';
import { getSchemePriceChart } from '@/src/services/schemeDetail';
import CustomTooltip from '../blocks/CustomTooltip';
import { SchemePeriodSwitch } from '../blocks/SchemePeriodSwitch';
import { SCHEME_CHART_DURARION_LEVELS } from '../../StockDetails/enums';
import styled from 'styled-components';

interface SchemeChartData {
  NAVRS: number;
  NAVDATE: string;
}

const NAVRETURNS = styled.p<{ positive: boolean }>`
  padding: 0 0 0 1rem;
  font-weight: 600;
  color: ${(props) =>
    props.positive ? 'green' : 'red'}; /* Green if positive, red if negative */
`;

const extractDurationAndTimeframe = (periodLevel: string) => {
  // TODO: TO Replace regex with string methods.
  // eslint-disable-next-line sonarjs/regular-expr
  const regex = new RegExp('^([A-Z]+)(\\d+)$');
  const match = regex.exec(periodLevel);

  if (match && match.length === 3) {
    const [, duration, timeframe] = match;
    return { duration, timeframe };
  }
  return { duration: 'M', timeframe: '1' };
};

const mfSchcode = document
  .getElementById('csr-scheme-details-area-chart')
  ?.getAttribute('data-mfSchcode');

const ONEMONTH = document
  .getElementById('csr-scheme-details-area-chart')
  ?.getAttribute('data-oneMonth');

const THREEMONTH = document
  .getElementById('csr-scheme-details-area-chart')
  ?.getAttribute('data-threeMonth');

const SIXMONTH = document
  .getElementById('csr-scheme-details-area-chart')
  ?.getAttribute('data-sixMonth');

const ONEYEAR = document
  .getElementById('csr-scheme-details-area-chart')
  ?.getAttribute('data-oneYear');

const THREEYEAR = document
  .getElementById('csr-scheme-details-area-chart')
  ?.getAttribute('data-threeYear');

const FIVEYEAR = document
  .getElementById('csr-scheme-details-area-chart')
  ?.getAttribute('data-fiveYear');

const INCEPTION = document
  .getElementById('csr-scheme-details-area-chart')
  ?.getAttribute('data-inception');

const SchemeAreaChart = () => {
  const [schemeChartData, setSchemeChartData] = useState<
    SchemeChartData[] | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodLevel, setPeriodLevel] = useState<string>(
    SCHEME_CHART_DURARION_LEVELS.ONEYEAR,
  );

  const handlePeriodSelect = (period: string) => {
    setPeriodLevel(period);
  };

  useEffect(() => {
    if (mfSchcode) {
      const { duration, timeframe } = extractDurationAndTimeframe(periodLevel);
      getSchemePriceChart(mfSchcode, duration, timeframe)
        .then((response) => {
          setSchemeChartData(response.data.data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to fetch chart data:', err);
          setError('Failed to load chart data');
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [periodLevel]);

  if (loading) {
    return <div>Loading chart...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  // Helper function to validate data
  /* eslint-disable sonarjs/function-return-type */
  const getValidValue = (
    value: string | undefined | null,
    fallback: number | string = '-',
  ) => {
    return value && !isNaN(parseFloat(value)) ? parseFloat(value) : fallback;
  };
  /* eslint-disable sonarjs/function-return-type */

  // Main logic for scheme returns
  // eslint-disable-next-line sonarjs/function-return-type
  const schemeReturnsPercentage = (() => {
    const defaultReturn =
      INCEPTION && !isNaN(parseFloat(INCEPTION)) ? parseFloat(INCEPTION) : '-';

    switch (periodLevel) {
      case SCHEME_CHART_DURARION_LEVELS.ONEMONTH:
        return getValidValue(ONEMONTH, defaultReturn);
      case SCHEME_CHART_DURARION_LEVELS.THREEMONTH:
        return getValidValue(THREEMONTH, defaultReturn);
      case SCHEME_CHART_DURARION_LEVELS.SIXMONTH:
        return getValidValue(SIXMONTH, defaultReturn);
      case SCHEME_CHART_DURARION_LEVELS.ONEYEAR:
        return getValidValue(ONEYEAR, defaultReturn);
      case SCHEME_CHART_DURARION_LEVELS.THREEYEAR:
        return getValidValue(THREEYEAR, defaultReturn);
      case SCHEME_CHART_DURARION_LEVELS.FIVEYEAR:
        return getValidValue(FIVEYEAR, defaultReturn);
      default:
        return getValidValue(undefined, defaultReturn); // Ensure `defaultReturn` fits the fallback type.
    }
  })();

  const isPositive =
    typeof schemeReturnsPercentage === 'number' && schemeReturnsPercentage > 0;

  const chartStrokeColor = isPositive ? '#339581' : '#E57373';
  const chartFillColor = isPositive
    ? { start: '#D8F4F0', end: '#F7FDFC' }
    : { start: '#F4D8D8', end: '#FCE7E7' };

  return (
    <>
      <NAVRETURNS positive={isPositive}>
        {typeof schemeReturnsPercentage === 'number'
          ? `${schemeReturnsPercentage.toFixed(2)}%`
          : '-'}
      </NAVRETURNS>
      <ResponsiveContainer width="100%" height={400}>
        <AreaChart data={schemeChartData || []}>
          <defs>
            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={chartFillColor.start}
                stopOpacity={1}
              />
              <stop
                offset="95%"
                stopColor={chartFillColor.end}
                stopOpacity={0.35}
              />
            </linearGradient>
          </defs>
          <XAxis dataKey="NAVDATE" hide reversed={true} />
          <YAxis domain={['auto', 'auto']} hide />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="NAVRS"
            stroke={chartStrokeColor}
            strokeWidth={1.6}
            fill="url(#colorValue)"
          />
        </AreaChart>
      </ResponsiveContainer>
      <SchemePeriodSwitch
        periodData={Object.values(SCHEME_CHART_DURARION_LEVELS)}
        onPeriodSelect={handlePeriodSelect}
      />
    </>
  );
};

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('csr-scheme-details-area-chart');
  if (container) {
    const root = createRoot(container);
    root.render(
      <Provider store={store}>
        <SchemeAreaChart />
      </Provider>,
    );
  } else {
    console.error('Target container is not a DOM element.');
  }
});
